import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiJson } from "../lib/queryClient";

type FeatureDef = { key: string; name: string; description: string };

type Price = {
  id: string;
  active: boolean;
  unitAmount: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  nickname: string | null;
  type: string;
};

type Package = {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  order: number | null;
  features: string[];
  defaultPriceId: string | null;
  prices: Price[];
};

type Coupon = {
  id: string;
  name: string | null;
  valid: boolean;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  redeemBy: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  appliesToProducts: string[] | null;
};

type PromotionCode = {
  id: string;
  code: string;
  active: boolean;
  couponId: string;
  couponName: string | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  expiresAt: number | null;
  restrictions: {
    firstTimeTransaction: boolean;
    minimumAmount: number | null;
    minimumAmountCurrency: string | null;
  };
};

type BookableCountry = { iso: string; name: string };

type CountryAddon = {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  country: string | null;
  countryName: string | null;
  defaultPriceId: string | null;
  prices: Price[];
};

function euro(cents: number | null, currency = "eur"): string {
  if (cents == null) return "—";
  try {
    return (cents / 100).toLocaleString("de-DE", {
      style: "currency",
      currency: currency.toUpperCase(),
    });
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function intervalLabel(interval: string | null): string {
  if (interval === "month") return "monatlich";
  if (interval === "year") return "jährlich";
  if (interval === "week") return "wöchentlich";
  if (interval === "day") return "täglich";
  return interval ?? "einmalig";
}

const btnPrimary =
  "px-3 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed";
const btnSubtle =
  "px-2.5 py-1.5 text-xs bg-[var(--color-app-bg-tertiary)] hover:bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text)] rounded disabled:opacity-40 disabled:cursor-not-allowed";
const inputCls = "w-full px-3 py-2 border border-[var(--color-app-border)] rounded text-sm";

/* ============================ PAKETE ============================ */

function PackageForm({
  features,
  initial,
  onSubmit,
  onCancel,
  busy,
}: {
  features: FeatureDef[];
  initial?: Package | null;
  onSubmit: (data: {
    name: string;
    description: string;
    order: number | null;
    features: string[];
    active: boolean;
  }) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [order, setOrder] = useState(
    initial?.order != null ? String(initial.order) : "",
  );
  const [active, setActive] = useState(initial?.active ?? true);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(initial?.features ?? []),
  );

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded-lg p-4 space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm">
          <span className="block text-[var(--color-app-text-secondary)] mb-1">Name</span>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="input-package-name"
            placeholder="z.B. Pro"
          />
        </label>
        <label className="text-sm">
          <span className="block text-[var(--color-app-text-secondary)] mb-1">Sortierung (Reihenfolge)</span>
          <input
            className={inputCls}
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            inputMode="numeric"
            placeholder="z.B. 1"
            data-testid="input-package-order"
          />
        </label>
      </div>
      <label className="text-sm block">
        <span className="block text-[var(--color-app-text-secondary)] mb-1">Beschreibung</span>
        <textarea
          className={inputCls}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          data-testid="input-package-description"
        />
      </label>

      <div>
        <span className="block text-[var(--color-app-text-secondary)] mb-2 text-sm">Enthaltene Funktionen</span>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {features.map((f) => (
            <label
              key={f.key}
              className="flex items-start gap-2 text-sm bg-white border border-[var(--color-app-border)] rounded px-2.5 py-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.has(f.key)}
                onChange={() => toggle(f.key)}
                className="mt-0.5"
                data-testid={`checkbox-feature-${f.key}`}
              />
              <span>
                <span className="block font-medium text-[var(--color-app-text)]">{f.name}</span>
                <span className="block text-xs text-[var(--color-app-text-secondary)]">{f.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          data-testid="checkbox-package-active"
        />
        <span className="text-[var(--color-app-text)]">Aktiv (im Verkauf sichtbar)</span>
      </label>

      <div className="flex gap-2">
        <button
          className={btnPrimary}
          disabled={busy || !name.trim()}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              description: description.trim(),
              order: order.trim() === "" ? null : Number(order),
              features: Array.from(selected),
              active,
            })
          }
          data-testid="button-save-package"
        >
          {initial ? "Speichern" : "Paket erstellen"}
        </button>
        <button className={btnSubtle} onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function PriceForm({
  onSubmit,
  onCancel,
  busy,
}: {
  onSubmit: (data: {
    unitAmount: number;
    interval: "month" | "year";
    nickname: string;
    setDefault: boolean;
  }) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [nickname, setNickname] = useState("");
  const [setDefault, setSetDefault] = useState(false);

  const euros = Number(amount.replace(",", "."));
  const valid = Number.isFinite(euros) && euros >= 0;

  return (
    <div className="bg-white border border-[var(--color-app-border)] rounded p-3 space-y-2 mt-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-xs">
          <span className="block text-[var(--color-app-text-secondary)] mb-1">Betrag (EUR)</span>
          <input
            className={inputCls}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="9,99"
            data-testid="input-price-amount"
          />
        </label>
        <label className="text-xs">
          <span className="block text-[var(--color-app-text-secondary)] mb-1">Intervall</span>
          <select
            className={inputCls}
            value={interval}
            onChange={(e) => setInterval(e.target.value as "month" | "year")}
            data-testid="select-price-interval"
          >
            <option value="month">monatlich</option>
            <option value="year">jährlich</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-[var(--color-app-text-secondary)] mb-1">Bezeichnung (optional)</span>
          <input
            className={inputCls}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            data-testid="input-price-nickname"
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs text-[var(--color-app-text)]">
        <input
          type="checkbox"
          checked={setDefault}
          onChange={(e) => setSetDefault(e.target.checked)}
        />
        Als Standardpreis des Pakets setzen
      </label>
      <div className="flex gap-2">
        <button
          className={btnPrimary}
          disabled={busy || !valid}
          onClick={() =>
            onSubmit({
              unitAmount: Math.round(euros * 100),
              interval,
              nickname: nickname.trim(),
              setDefault,
            })
          }
          data-testid="button-save-price"
        >
          Preis hinzufügen
        </button>
        <button className={btnSubtle} onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function PackagesTab({
  features,
  onError,
}: {
  features: FeatureDef[];
  onError: (m: string) => void;
}) {
  const [packages, setPackages] = useState<Package[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingPriceFor, setAddingPriceFor] = useState<string | null>(null);

  async function reload() {
    const data = await apiJson<{ packages: Package[] }>(
      "GET",
      "/api/admin/subscription/packages",
    );
    setPackages(data.packages);
  }

  useEffect(() => {
    reload().catch((e) => onError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function featureNames(keys: string[]): string {
    return keys
      .map((k) => features.find((f) => f.key === k)?.name ?? k)
      .join(", ");
  }

  async function createPackage(data: {
    name: string;
    description: string;
    order: number | null;
    features: string[];
    active: boolean;
  }) {
    setBusy(true);
    try {
      await apiJson("POST", "/api/admin/subscription/packages", data);
      setCreating(false);
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function updatePackage(
    id: string,
    data: {
      name: string;
      description: string;
      order: number | null;
      features: string[];
      active: boolean;
    },
  ) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/packages/${id}`, data);
      setEditingId(null);
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(p: Package) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/packages/${p.id}`, {
        active: !p.active,
      });
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addPrice(
    productId: string,
    data: {
      unitAmount: number;
      interval: "month" | "year";
      nickname: string;
      setDefault: boolean;
    },
  ) {
    setBusy(true);
    try {
      await apiJson(
        "POST",
        `/api/admin/subscription/packages/${productId}/prices`,
        data,
      );
      setAddingPriceFor(null);
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function togglePrice(price: Price) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/prices/${price.id}`, {
        active: !price.active,
      });
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!packages) return <div className="text-[var(--color-app-text-secondary)]">Lädt …</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Pakete sind Stripe-Produkte. Funktionen werden in den Produkt-Metadaten
          gespeichert.
        </p>
        {!creating && (
          <button
            className={btnPrimary}
            onClick={() => {
              setCreating(true);
              setEditingId(null);
            }}
            data-testid="button-new-package"
          >
            Neues Paket
          </button>
        )}
      </div>

      {creating && (
        <PackageForm
          features={features}
          onSubmit={createPackage}
          onCancel={() => setCreating(false)}
          busy={busy}
        />
      )}

      {packages.length === 0 && !creating ? (
        <div className="text-[var(--color-app-text-tertiary)] text-sm italic">
          Noch keine Pakete angelegt.
        </div>
      ) : null}

      {packages.map((p) => (
        <div
          key={p.id}
          className="bg-white border border-[var(--color-app-border)] rounded-lg overflow-hidden"
          data-testid={`card-package-${p.id}`}
        >
          <div className="px-4 py-3 border-b border-[var(--color-app-border)] flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-[var(--color-app-text)]">{p.name}</h3>
                {p.active ? (
                  <span className="px-1.5 py-0.5 rounded bg-[var(--color-go-bg)] text-[var(--color-go)] text-[10px] font-semibold uppercase">
                    Aktiv
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)] text-[10px] font-semibold uppercase">
                    Inaktiv
                  </span>
                )}
                {p.order != null && (
                  <span className="text-xs text-[var(--color-app-text-tertiary)]">#{p.order}</span>
                )}
              </div>
              {p.description && (
                <p className="text-sm text-[var(--color-app-text-secondary)] mt-0.5">{p.description}</p>
              )}
              <p className="text-xs text-[var(--color-app-text-tertiary)] mt-1 font-mono">{p.id}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                className={btnSubtle}
                onClick={() => {
                  setEditingId(editingId === p.id ? null : p.id);
                  setCreating(false);
                }}
                data-testid={`button-edit-package-${p.id}`}
              >
                Bearbeiten
              </button>
              <button
                className={btnSubtle}
                disabled={busy}
                onClick={() => toggleActive(p)}
                data-testid={`button-toggle-package-${p.id}`}
              >
                {p.active ? "Deaktivieren" : "Aktivieren"}
              </button>
            </div>
          </div>

          {editingId === p.id ? (
            <div className="p-4">
              <PackageForm
                features={features}
                initial={p}
                onSubmit={(d) => updatePackage(p.id, d)}
                onCancel={() => setEditingId(null)}
                busy={busy}
              />
            </div>
          ) : (
            <div className="px-4 py-3 space-y-3">
              <div>
                <span className="text-xs uppercase tracking-wide text-[var(--color-app-text-tertiary)]">
                  Funktionen
                </span>
                <p className="text-sm text-[var(--color-app-text)] mt-0.5">
                  {p.features.length > 0 ? (
                    featureNames(p.features)
                  ) : (
                    <span className="text-[var(--color-app-text-tertiary)] italic">Keine zugewiesen</span>
                  )}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-[var(--color-app-text-tertiary)]">
                    Preise
                  </span>
                  <button
                    className={btnSubtle}
                    onClick={() =>
                      setAddingPriceFor(addingPriceFor === p.id ? null : p.id)
                    }
                    data-testid={`button-add-price-${p.id}`}
                  >
                    Preis hinzufügen
                  </button>
                </div>
                {p.prices.length === 0 ? (
                  <p className="text-sm text-[var(--color-app-text-tertiary)] italic mt-1">
                    Keine Preise hinterlegt.
                  </p>
                ) : (
                  <ul className="mt-1 divide-y divide-[var(--color-app-border)]">
                    {p.prices.map((pr) => (
                      <li
                        key={pr.id}
                        className="flex items-center justify-between py-1.5 text-sm"
                        data-testid={`row-price-${pr.id}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-medium text-[var(--color-app-text)]">
                            {euro(pr.unitAmount, pr.currency)}
                          </span>
                          <span className="text-[var(--color-app-text-secondary)]">
                            {intervalLabel(pr.interval)}
                          </span>
                          {pr.nickname && (
                            <span className="text-xs text-[var(--color-app-text-tertiary)]">
                              · {pr.nickname}
                            </span>
                          )}
                          {p.defaultPriceId === pr.id && (
                            <span className="px-1.5 py-0.5 rounded bg-[var(--color-brand-50)] text-[var(--color-brand-600)] text-[10px] font-semibold uppercase">
                              Standard
                            </span>
                          )}
                          {!pr.active && (
                            <span className="px-1.5 py-0.5 rounded bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)] text-[10px] font-semibold uppercase">
                              Archiviert
                            </span>
                          )}
                        </span>
                        <button
                          className={btnSubtle}
                          disabled={busy}
                          onClick={() => togglePrice(pr)}
                          data-testid={`button-toggle-price-${pr.id}`}
                        >
                          {pr.active ? "Archivieren" : "Aktivieren"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {addingPriceFor === p.id && (
                  <PriceForm
                    onSubmit={(d) => addPrice(p.id, d)}
                    onCancel={() => setAddingPriceFor(null)}
                    busy={busy}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ============================ RABATTCODES ============================ */

function DiscountsTab({ onError }: { onError: (m: string) => void }) {
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);
  const [promos, setPromos] = useState<PromotionCode[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);

  // Coupon form state
  const [cName, setCName] = useState("");
  const [cKind, setCKind] = useState<"percent" | "amount">("percent");
  const [cValue, setCValue] = useState("");
  const [cDuration, setCDuration] = useState<"once" | "repeating" | "forever">(
    "once",
  );
  const [cMonths, setCMonths] = useState("");
  const [cMax, setCMax] = useState("");

  // Promo form state
  const [pCoupon, setPCoupon] = useState("");
  const [pCode, setPCode] = useState("");
  const [pMax, setPMax] = useState("");
  const [pExpires, setPExpires] = useState("");

  async function reload() {
    const data = await apiJson<{
      coupons: Coupon[];
      promotionCodes: PromotionCode[];
    }>("GET", "/api/admin/subscription/discounts");
    setCoupons(data.coupons);
    setPromos(data.promotionCodes);
  }

  useEffect(() => {
    reload().catch((e) => onError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function couponLabel(c: Coupon): string {
    if (c.percentOff != null) return `${c.percentOff}%`;
    if (c.amountOff != null) return euro(c.amountOff, c.currency ?? "eur");
    return "—";
  }
  function durationLabel(c: Coupon): string {
    if (c.duration === "forever") return "dauerhaft";
    if (c.duration === "once") return "einmalig";
    if (c.duration === "repeating")
      return `${c.durationInMonths ?? "?"} Monate`;
    return c.duration;
  }

  async function createCoupon() {
    const value = Number(cValue.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      onError("Bitte einen gültigen Rabattwert eingeben.");
      return;
    }
    const body: Record<string, unknown> = {
      name: cName.trim() || null,
      duration: cDuration,
    };
    if (cKind === "percent") body.percentOff = value;
    else body.amountOff = Math.round(value * 100);
    if (cDuration === "repeating") body.durationInMonths = Number(cMonths);
    if (cMax.trim()) body.maxRedemptions = Number(cMax);

    setBusy(true);
    try {
      await apiJson("POST", "/api/admin/subscription/coupons", body);
      setShowCouponForm(false);
      setCName("");
      setCValue("");
      setCMonths("");
      setCMax("");
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteCoupon(c: Coupon) {
    if (!confirm(`Coupon "${c.name || c.id}" löschen?`)) return;
    setBusy(true);
    try {
      await apiJson("DELETE", `/api/admin/subscription/coupons/${c.id}`);
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function createPromo() {
    if (!pCoupon) {
      onError("Bitte einen Coupon auswählen.");
      return;
    }
    const body: Record<string, unknown> = { couponId: pCoupon };
    if (pCode.trim()) body.code = pCode.trim();
    if (pMax.trim()) body.maxRedemptions = Number(pMax);
    if (pExpires) body.expiresAt = Math.floor(new Date(pExpires).getTime() / 1000);

    setBusy(true);
    try {
      await apiJson("POST", "/api/admin/subscription/promotion-codes", body);
      setShowPromoForm(false);
      setPCode("");
      setPMax("");
      setPExpires("");
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function togglePromo(pc: PromotionCode) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/promotion-codes/${pc.id}`, {
        active: !pc.active,
      });
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!coupons || !promos) return <div className="text-[var(--color-app-text-secondary)]">Lädt …</div>;

  return (
    <div className="space-y-6">
      {/* Coupons */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[var(--color-app-text)]">Coupons</h3>
          {!showCouponForm && (
            <button
              className={btnPrimary}
              onClick={() => setShowCouponForm(true)}
              data-testid="button-new-coupon"
            >
              Neuer Coupon
            </button>
          )}
        </div>
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Ein Coupon definiert den Rabatt. Promotion-Codes (unten) sind die vom
          Kunden eingebbaren Codes, die auf einen Coupon verweisen.
        </p>

        {showCouponForm && (
          <div className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded-lg p-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">Name (intern)</span>
                <input
                  className={inputCls}
                  value={cName}
                  onChange={(e) => setCName(e.target.value)}
                  data-testid="input-coupon-name"
                  placeholder="z.B. Sommer 2026"
                />
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">Rabatt-Art</span>
                <select
                  className={inputCls}
                  value={cKind}
                  onChange={(e) => setCKind(e.target.value as "percent" | "amount")}
                  data-testid="select-coupon-kind"
                >
                  <option value="percent">Prozent (%)</option>
                  <option value="amount">Fester Betrag (EUR)</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">
                  {cKind === "percent" ? "Prozent" : "Betrag (EUR)"}
                </span>
                <input
                  className={inputCls}
                  value={cValue}
                  onChange={(e) => setCValue(e.target.value)}
                  inputMode="decimal"
                  data-testid="input-coupon-value"
                />
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">Dauer</span>
                <select
                  className={inputCls}
                  value={cDuration}
                  onChange={(e) =>
                    setCDuration(
                      e.target.value as "once" | "repeating" | "forever",
                    )
                  }
                  data-testid="select-coupon-duration"
                >
                  <option value="once">Einmalig</option>
                  <option value="repeating">Wiederkehrend</option>
                  <option value="forever">Dauerhaft</option>
                </select>
              </label>
              {cDuration === "repeating" && (
                <label className="text-sm">
                  <span className="block text-[var(--color-app-text-secondary)] mb-1">Anzahl Monate</span>
                  <input
                    className={inputCls}
                    value={cMonths}
                    onChange={(e) => setCMonths(e.target.value)}
                    inputMode="numeric"
                    data-testid="input-coupon-months"
                  />
                </label>
              )}
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">
                  Max. Einlösungen (optional)
                </span>
                <input
                  className={inputCls}
                  value={cMax}
                  onChange={(e) => setCMax(e.target.value)}
                  inputMode="numeric"
                  data-testid="input-coupon-max"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className={btnPrimary}
                disabled={busy}
                onClick={createCoupon}
                data-testid="button-save-coupon"
              >
                Coupon erstellen
              </button>
              <button
                className={btnSubtle}
                onClick={() => setShowCouponForm(false)}
                disabled={busy}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto bg-white border border-[var(--color-app-border)] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-app-bg-secondary)] text-left text-[var(--color-app-text-secondary)] text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Rabatt</th>
                <th className="px-3 py-2">Dauer</th>
                <th className="px-3 py-2 text-center">Eingelöst</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-t border-[var(--color-app-border)]">
                  <td className="px-3 py-2">
                    {c.name || <span className="text-[var(--color-app-text-tertiary)]">—</span>}
                    <div className="text-[10px] font-mono text-[var(--color-app-text-tertiary)]">{c.id}</div>
                  </td>
                  <td className="px-3 py-2 font-medium">{couponLabel(c)}</td>
                  <td className="px-3 py-2 text-[var(--color-app-text-secondary)]">{durationLabel(c)}</td>
                  <td className="px-3 py-2 text-center">
                    {c.timesRedeemed}
                    {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ""}
                  </td>
                  <td className="px-3 py-2">
                    {c.valid ? (
                      <span className="text-[var(--color-go)]">gültig</span>
                    ) : (
                      <span className="text-[var(--color-app-text-tertiary)]">ungültig</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="px-2 py-1 text-xs bg-[var(--color-nogo-bg)] hover:bg-[var(--color-nogo-bg)] text-[var(--color-nogo)] rounded disabled:opacity-40"
                      disabled={busy}
                      onClick={() => deleteCoupon(c)}
                      data-testid={`button-delete-coupon-${c.id}`}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-[var(--color-app-text-tertiary)]">
                    Keine Coupons.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Promotion codes */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[var(--color-app-text)]">Promotion-Codes</h3>
          {!showPromoForm && (
            <button
              className={btnPrimary}
              onClick={() => {
                setShowPromoForm(true);
                if (!pCoupon && coupons[0]) setPCoupon(coupons[0].id);
              }}
              disabled={coupons.length === 0}
              title={coupons.length === 0 ? "Zuerst einen Coupon anlegen" : undefined}
              data-testid="button-new-promo"
            >
              Neuer Promotion-Code
            </button>
          )}
        </div>

        {showPromoForm && (
          <div className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded-lg p-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">Coupon</span>
                <select
                  className={inputCls}
                  value={pCoupon}
                  onChange={(e) => setPCoupon(e.target.value)}
                  data-testid="select-promo-coupon"
                >
                  {coupons.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.id} ({couponLabel(c)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">
                  Code (optional, sonst automatisch)
                </span>
                <input
                  className={inputCls}
                  value={pCode}
                  onChange={(e) => setPCode(e.target.value.toUpperCase())}
                  data-testid="input-promo-code"
                  placeholder="z.B. SOMMER25"
                />
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">
                  Max. Einlösungen (optional)
                </span>
                <input
                  className={inputCls}
                  value={pMax}
                  onChange={(e) => setPMax(e.target.value)}
                  inputMode="numeric"
                  data-testid="input-promo-max"
                />
              </label>
              <label className="text-sm">
                <span className="block text-[var(--color-app-text-secondary)] mb-1">Gültig bis (optional)</span>
                <input
                  type="date"
                  className={inputCls}
                  value={pExpires}
                  onChange={(e) => setPExpires(e.target.value)}
                  data-testid="input-promo-expires"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                className={btnPrimary}
                disabled={busy}
                onClick={createPromo}
                data-testid="button-save-promo"
              >
                Promotion-Code erstellen
              </button>
              <button
                className={btnSubtle}
                onClick={() => setShowPromoForm(false)}
                disabled={busy}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto bg-white border border-[var(--color-app-border)] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-app-bg-secondary)] text-left text-[var(--color-app-text-secondary)] text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Coupon</th>
                <th className="px-3 py-2 text-center">Eingelöst</th>
                <th className="px-3 py-2">Gültig bis</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((pc) => (
                <tr key={pc.id} className="border-t border-[var(--color-app-border)]">
                  <td className="px-3 py-2 font-mono font-semibold">{pc.code}</td>
                  <td className="px-3 py-2 text-[var(--color-app-text-secondary)]">
                    {pc.couponName || pc.couponId}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {pc.timesRedeemed}
                    {pc.maxRedemptions != null ? ` / ${pc.maxRedemptions}` : ""}
                  </td>
                  <td className="px-3 py-2 text-[var(--color-app-text-secondary)]">
                    {pc.expiresAt
                      ? new Date(pc.expiresAt * 1000).toLocaleDateString("de-DE")
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {pc.active ? (
                      <span className="text-[var(--color-go)]">aktiv</span>
                    ) : (
                      <span className="text-[var(--color-app-text-tertiary)]">inaktiv</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className={btnSubtle}
                      disabled={busy}
                      onClick={() => togglePromo(pc)}
                      data-testid={`button-toggle-promo-${pc.id}`}
                    >
                      {pc.active ? "Deaktivieren" : "Aktivieren"}
                    </button>
                  </td>
                </tr>
              ))}
              {promos.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-[var(--color-app-text-tertiary)]">
                    Keine Promotion-Codes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ============================ ROOT ============================ */

/* ===================== LÄNDER-ADD-ONS (Task #522) ===================== */

function CountryAddonsTab({ onError }: { onError: (m: string) => void }) {
  const [addons, setAddons] = useState<CountryAddon[] | null>(null);
  const [catalog, setCatalog] = useState<BookableCountry[]>([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCountry, setNewCountry] = useState("");
  const [newName, setNewName] = useState("");
  const [addingPriceFor, setAddingPriceFor] = useState<string | null>(null);

  async function reload() {
    try {
      const d = await apiJson<{
        addons: CountryAddon[];
        countryCatalog: BookableCountry[];
      }>("GET", "/api/admin/subscription/country-addons");
      setAddons(d.addons);
      setCatalog(d.countryCatalog);
    } catch (e) {
      onError((e as Error).message);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Länder, für die noch KEIN aktives Add-on existiert.
  const availableCountries = useMemo(() => {
    const taken = new Set(
      (addons ?? [])
        .filter((a) => a.active && a.country)
        .map((a) => a.country as string),
    );
    return catalog.filter((c) => !taken.has(c.iso));
  }, [addons, catalog]);

  async function createAddon() {
    if (!newCountry) return;
    setBusy(true);
    try {
      await apiJson("POST", "/api/admin/subscription/country-addons", {
        country: newCountry,
        name: newName.trim() || undefined,
      });
      setCreating(false);
      setNewCountry("");
      setNewName("");
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(a: CountryAddon) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/country-addons/${a.id}`, {
        active: !a.active,
      });
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addPrice(
    addonId: string,
    data: {
      unitAmount: number;
      interval: "month" | "year";
      nickname: string;
      setDefault: boolean;
    },
  ) {
    setBusy(true);
    try {
      await apiJson("POST", `/api/admin/subscription/packages/${addonId}/prices`, {
        unitAmount: data.unitAmount,
        currency: "eur",
        interval: data.interval,
        nickname: data.nickname || undefined,
        setDefault: data.setDefault,
      });
      setAddingPriceFor(null);
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function togglePrice(price: Price) {
    setBusy(true);
    try {
      await apiJson("PATCH", `/api/admin/subscription/prices/${price.id}`, {
        active: !price.active,
      });
      await reload();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-[var(--color-app-text-secondary)] max-w-2xl">
          Jedes Abo enthält genau ein Basis-Land (Auswahl beim Kauf). Weitere
          Länder werden hier als einzeln buchbare Add-ons mit eigenem Preis
          angelegt. Nur Länder mit einem aktiven Add-on (und Preis) sind für
          Nutzer buchbar.
        </p>
        <button
          className={btnPrimary}
          onClick={() => setCreating((v) => !v)}
          data-testid="button-new-country-addon"
        >
          {creating ? "Abbrechen" : "Land anlegen"}
        </button>
      </div>

      {creating ? (
        <div className="bg-white border border-[var(--color-app-border)] rounded p-3 space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs">
              <span className="block text-[var(--color-app-text-secondary)] mb-1">Land</span>
              <select
                className={inputCls}
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                data-testid="select-country-addon"
              >
                <option value="">— Land wählen —</option>
                {availableCountries.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.name} ({c.iso})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              <span className="block text-[var(--color-app-text-secondary)] mb-1">
                Bezeichnung (optional)
              </span>
              <input
                className={inputCls}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="z. B. Land: Österreich"
                data-testid="input-country-addon-name"
              />
            </label>
          </div>
          <button
            className={btnPrimary}
            disabled={busy || !newCountry}
            onClick={() => void createAddon()}
            data-testid="button-save-country-addon"
          >
            Add-on erstellen
          </button>
        </div>
      ) : null}

      {addons == null ? (
        <p className="text-sm text-[var(--color-app-text-secondary)]">Lädt…</p>
      ) : addons.length === 0 ? (
        <div className="text-sm text-[var(--color-app-text-secondary)] bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded p-4">
          Noch keine Länder-Add-ons angelegt.
        </div>
      ) : (
        <div className="space-y-3">
          {addons.map((a) => (
            <div
              key={a.id}
              className="bg-white border border-[var(--color-app-border)] rounded p-3 space-y-2"
              data-testid={`card-country-addon-${a.country ?? a.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-[var(--color-app-text)]">
                    {a.countryName ?? a.country ?? a.name}
                    {a.country ? (
                      <span className="text-[var(--color-app-text-tertiary)] font-normal">
                        {" "}
                        ({a.country})
                      </span>
                    ) : null}
                    {!a.active ? (
                      <span className="ml-2 text-xs text-[var(--color-app-text-secondary)]">
                        (inaktiv)
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-[var(--color-app-text-secondary)]">{a.name}</div>
                </div>
                <button
                  className={btnSubtle}
                  onClick={() => void toggleActive(a)}
                  disabled={busy}
                  data-testid={`button-toggle-country-addon-${a.country ?? a.id}`}
                >
                  {a.active ? "Deaktivieren" : "Aktivieren"}
                </button>
              </div>

              <div className="pl-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[var(--color-app-text-secondary)]">
                    Preise
                  </span>
                  <button
                    className={btnSubtle}
                    onClick={() =>
                      setAddingPriceFor(addingPriceFor === a.id ? null : a.id)
                    }
                    disabled={busy}
                    data-testid={`button-add-price-${a.country ?? a.id}`}
                  >
                    {addingPriceFor === a.id ? "Abbrechen" : "Preis hinzufügen"}
                  </button>
                </div>
                {a.prices.length === 0 ? (
                  <p className="text-xs text-[var(--color-app-text-tertiary)] mt-1">
                    Kein Preis hinterlegt — Land ist noch nicht buchbar.
                  </p>
                ) : (
                  <ul className="mt-1 space-y-1">
                    {a.prices.map((pr) => (
                      <li
                        key={pr.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className={pr.active ? "" : "text-[var(--color-app-text-tertiary)] line-through"}>
                          {euro(pr.unitAmount, pr.currency)} {intervalLabel(pr.interval)}
                          {pr.id === a.defaultPriceId ? (
                            <span className="ml-2 text-xs text-brand-600">
                              Standard
                            </span>
                          ) : null}
                        </span>
                        <button
                          className={btnSubtle}
                          onClick={() => void togglePrice(pr)}
                          disabled={busy}
                        >
                          {pr.active ? "Deaktivieren" : "Aktivieren"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {addingPriceFor === a.id ? (
                  <PriceForm
                    busy={busy}
                    onCancel={() => setAddingPriceFor(null)}
                    onSubmit={(data) => void addPrice(a.id, data)}
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminSubscriptions() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"packages" | "countries" | "discounts">(
    "packages",
  );
  const [features, setFeatures] = useState<FeatureDef[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.isAdmin) return;
    apiJson<{ features: FeatureDef[] }>("GET", "/api/admin/subscription/features")
      .then((d) => setFeatures(d.features))
      .catch((e) => setError((e as Error).message));
  }, [user?.isAdmin]);

  const tabBtn = (
    key: "packages" | "countries" | "discounts",
    label: string,
  ) => (
    <button
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${
        tab === key
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-[var(--color-app-text-secondary)] hover:text-[var(--color-app-text)]"
      }`}
      data-testid={`tab-sub-${key}`}
    >
      {label}
    </button>
  );

  const errorBanner = useMemo(
    () =>
      error ? (
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded px-3 py-2 flex justify-between items-center">
          <span>{error}</span>
          <button className="text-[var(--color-nogo)] underline" onClick={() => setError(null)}>
            Schließen
          </button>
        </div>
      ) : null,
    [error],
  );

  if (!user?.isAdmin) {
    return (
      <div className="p-6 bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] text-[var(--color-nogo)] rounded-md">
        Diese Seite ist nur für Admins.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/admin" className="text-sm text-brand-600 hover:underline">
        ← Zurück zur Nutzerverwaltung
      </Link>
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-app-text)]">Abos & Pakete</h1>
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Pakete, Preise und Rabattcodes verwalten. Stripe ist die Quelle der
          Wahrheit.
        </p>
      </header>

      {errorBanner}

      <div className="border-b border-[var(--color-app-border)] flex gap-1">
        {tabBtn("packages", "Pakete & Preise")}
        {tabBtn("countries", "Länder-Add-ons")}
        {tabBtn("discounts", "Rabattcodes")}
      </div>

      {tab === "packages" ? (
        <PackagesTab features={features} onError={setError} />
      ) : tab === "countries" ? (
        <CountryAddonsTab onError={setError} />
      ) : (
        <DiscountsTab onError={setError} />
      )}
    </div>
  );
}
